import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Loader2, TrendingDown, TrendingUp, Star, Scale, Package,
  FileSignature, ChevronDown, ChevronUp,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ==================== Types ====================

interface Product {
  id: string;
  name: string;
  sku: string;
}

interface VendorRateOrder {
  poId: string;
  poNumber: string;
  date: string;
  rate: number;
  quantity: number;
  amount: number;
  status: string;
}

interface VendorRate {
  vendorId: string;
  vendorName: string;
  rating: number | null;
  orderCount: number;
  avgRate: number;
  minRate: number;
  maxRate: number;
  latestRate: number;
  totalQty: number;
  orders: VendorRateOrder[];
}

interface ContractRate {
  contractId: string;
  contractNumber: string;
  contractTitle: string;
  vendorId: string;
  vendorName: string;
  rate: number;
  validTo: string;
}

interface RateData {
  product: Product;
  vendorRates: VendorRate[];
  contractRates: ContractRate[];
}

// ==================== Component ====================

export function VendorRateComparisonPage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState(searchParams.get('productId') || '');
  const [productSearch, setProductSearch] = useState('');
  const [rateData, setRateData] = useState<RateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);

  // Fetch products list
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get('/products', { params: { limit: 500 } });
        setProducts(res.data.data || []);
      } catch { /* ignore */ }
      finally { setProductsLoading(false); }
    };
    fetchProducts();
  }, []);

  // Fetch vendor rates when product selected
  const fetchRates = useCallback(async (pid: string) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await api.get('/purchase-dashboard/vendor-rates', { params: { productId: pid } });
      setRateData(res.data.data);
    } catch {
      setRateData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedProductId) fetchRates(selectedProductId);
  }, [selectedProductId, fetchRates]);

  const filteredProducts = products.filter((p) => {
    if (!productSearch) return true;
    const s = productSearch.toLowerCase();
    return (p.name?.toLowerCase().includes(s) || p.sku?.toLowerCase().includes(s));
  });

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Scale className="w-6 h-6" />
          Vendor Rate Comparison
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Compare vendor rates, historical pricing, and contract rates for any product
        </p>
      </div>

      {/* Product Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Product / Material
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search products by name or SKU..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {(productSearch || !selectedProductId) && (
          <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
            {productsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <p className="text-sm text-gray-400 py-3 text-center">No products found</p>
            ) : (
              filteredProducts.slice(0, 20).map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedProductId(p.id); setProductSearch(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${
                    selectedProductId === p.id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <span className="font-medium">{p.name}</span>
                  {p.sku && <span className="ml-2 text-gray-400 text-xs">({p.sku})</span>}
                </button>
              ))
            )}
          </div>
        )}
        {selectedProductId && rateData?.product && !productSearch && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <Package className="w-4 h-4 text-blue-500" />
            <span className="font-medium text-gray-900 dark:text-white">{rateData.product.name}</span>
            {rateData.product.sku && <span className="text-gray-400">({rateData.product.sku})</span>}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      )}

      {/* Rate Comparison Results */}
      {!loading && rateData && (
        <div className="space-y-6">
          {/* Contract Rates */}
          {rateData.contractRates.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <FileSignature className="w-4 h-4 text-purple-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active Contract Rates</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">Contract</th>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">Vendor</th>
                      <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Rate</th>
                      <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Valid Until</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {rateData.contractRates.map((cr) => (
                      <tr key={cr.contractId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">
                          {cr.contractNumber}
                          <span className="block text-xs text-gray-400">{cr.contractTitle}</span>
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{cr.vendorName}</td>
                        <td className="px-4 py-2 text-right font-semibold text-purple-600 dark:text-purple-400">
                          {formatINR(cr.rate)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-500">{formatDate(cr.validTo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Vendor Rate Comparison Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <Scale className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Vendor Rate Comparison ({rateData.vendorRates.length} vendors)
              </h3>
            </div>

            {rateData.vendorRates.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                No purchase history found for this product
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {rateData.vendorRates.map((v, idx) => {
                  const isExpanded = expandedVendor === v.vendorId;
                  const bestRate = rateData.vendorRates[0]?.latestRate;
                  const isBest = idx === 0 && rateData.vendorRates.length > 1;

                  return (
                    <div key={v.vendorId}>
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                        onClick={() => setExpandedVendor(isExpanded ? null : v.vendorId)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            isBest ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}>
                            #{idx + 1}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              {v.vendorName}
                              {isBest && (
                                <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                  BEST RATE
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {v.orderCount} orders | Total qty: {v.totalQty.toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {v.rating && (
                            <div className="flex items-center gap-0.5 text-yellow-500">
                              <Star className="w-3 h-3 fill-current" />
                              <span className="text-xs">{v.rating}</span>
                            </div>
                          )}
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{formatINR(v.latestRate)}</p>
                            <p className="text-[10px] text-gray-400">latest rate</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600 dark:text-gray-300">{formatINR(v.avgRate)}</p>
                            <p className="text-[10px] text-gray-400">avg</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {formatINR(v.minRate)} - {formatINR(v.maxRate)}
                            </p>
                            <p className="text-[10px] text-gray-400">range</p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Expanded: Order History */}
                      {isExpanded && v.orders.length > 0 && (
                        <div className="px-4 pb-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 dark:text-gray-400">
                                <th className="px-2 py-1 text-left">PO Number</th>
                                <th className="px-2 py-1 text-left">Date</th>
                                <th className="px-2 py-1 text-right">Rate</th>
                                <th className="px-2 py-1 text-right">Qty</th>
                                <th className="px-2 py-1 text-right">Amount</th>
                                <th className="px-2 py-1 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                              {v.orders.map((o, oi) => {
                                const prevRate = oi < v.orders.length - 1 ? v.orders[oi + 1].rate : null;
                                const trend = prevRate !== null ? (o.rate < prevRate ? 'down' : o.rate > prevRate ? 'up' : 'flat') : 'flat';
                                return (
                                  <tr key={o.poId} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                                    <td className="px-2 py-1.5 text-blue-600 dark:text-blue-400 font-medium">{o.poNumber}</td>
                                    <td className="px-2 py-1.5 text-gray-500">{formatDate(o.date)}</td>
                                    <td className="px-2 py-1.5 text-right font-medium text-gray-900 dark:text-white flex items-center justify-end gap-1">
                                      {formatINR(o.rate)}
                                      {trend === 'down' && <TrendingDown className="w-3 h-3 text-green-500" />}
                                      {trend === 'up' && <TrendingUp className="w-3 h-3 text-red-500" />}
                                    </td>
                                    <td className="px-2 py-1.5 text-right text-gray-600 dark:text-gray-300">{o.quantity.toLocaleString('en-IN')}</td>
                                    <td className="px-2 py-1.5 text-right text-gray-600 dark:text-gray-300">{formatINR(o.amount)}</td>
                                    <td className="px-2 py-1.5 text-center">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        o.status === 'received' ? 'bg-green-100 text-green-700' :
                                        o.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>
                                        {o.status}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No product selected */}
      {!loading && !rateData && selectedProductId && (
        <div className="text-center py-12 text-gray-400">
          No data found for this product
        </div>
      )}
      {!selectedProductId && !loading && (
        <div className="text-center py-12 text-gray-400">
          Select a product above to compare vendor rates
        </div>
      )}
    </div>
  );
}
